import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAccount } from "wagmi";
import { formatEnglishDate } from "./utils/dateFormatting";
import LogoAppleTreeHD from "./LogoAppleTreeHD.svg";
import { getIpfsUrl } from "./utils/ipfs";
import LegalFooter from "./components/legal/LegalFooter";
import LevelBadge from "./components/certification/LevelBadge";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").trim().replace(/\/$/, "");

const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchCompanyData();
  }, [id]);

  const fetchCompanyData = async () => {
    if (!id) {
      setError("Company ID is required");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Try public endpoint first, fallback to companies endpoint
      let res = await fetch(`${API_BASE_URL}/api/public/certified-companies/${id}`);
      
      if (!res.ok) {
        // Fallback to regular companies endpoint
        res = await fetch(`${API_BASE_URL}/api/companies/${id}`);
      }

      if (!res.ok) {
        throw new Error("Company not found");
      }

      const data = await res.json();
      setCompany(data);
    } catch (err) {
      console.error("Error fetching company:", err);
      setError(err.message || "Failed to load company data");
    } finally {
      setLoading(false);
    }
  };

  const getLocationVisibilityLabel = (mapVisibility) => {
    switch(mapVisibility) {
      case "EXACT_ADDRESS": return "Exact address";
      case "CITY_REGION": return "City/Region (approximate)";
      case "COUNTRY_ONLY": return "Country only (approximate)";
      case "HIDDEN": return "Hidden";
      default: return "Not specified";
    }
  };

  const getDisplayLocation = () => {
    if (!company) return null;
    
    // Only show exact address if explicitly consented
    if (company.mapVisibility === "EXACT_ADDRESS" && company.address) {
      return company.address;
    }
    
    // For CITY_REGION, show city and country
    if (company.mapVisibility === "CITY_REGION") {
      const parts = [];
      if (company.city) parts.push(company.city);
      if (company.region) parts.push(company.region);
      if (company.country) parts.push(company.country);
      return parts.length > 0 ? parts.join(", ") : company.country || "Location approximate";
    }
    
    // For COUNTRY_ONLY, show only country
    if (company.mapVisibility === "COUNTRY_ONLY") {
      return company.country || "Country level";
    }
    
    // For HIDDEN or default
    return "Location not disclosed";
  };

  const getScoreColor = (score) => {
    if (!score) return "#6b7280";
    const numScore = typeof score === "string" ? parseFloat(score) : score;
    if (numScore >= 80) return "#22c55e";
    if (numScore >= 60) return "#eab308";
    if (numScore >= 40) return "#f97316";
    return "#ef4444";
  };

  const renderScoreCard = (label, score, maxScore = 100) => {
    const percentage = score ? (score / maxScore) * 100 : 0;
    const color = getScoreColor(percentage);
    
    return (
      <div style={{
        padding: "16px",
        background: "rgba(30,41,59,0.5)",
        borderRadius: "8px",
        border: "1px solid rgba(148,163,184,0.3)",
        textAlign: "center"
      }}>
        <div style={{ fontSize: "12px", color: "#94a3b8", marginBottom: "8px", fontWeight: "600" }}>
          {label}
        </div>
        <div style={{ 
          fontSize: "32px", 
          fontWeight: "800", 
          color: color,
          textShadow: `0 0 10px ${color}40`
        }}>
          {score || "N/A"}
        </div>
        {maxScore && score && (
          <div style={{ fontSize: "11px", color: "#64748b", marginTop: "4px" }}>
            out of {maxScore}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: "100vh", 
        background: "linear-gradient(135deg, #050816 0%, #0a1628 100%)", 
        color: "#f5f5f5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "18px", fontWeight: "600" }}>Loading company data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: "100vh", 
        background: "linear-gradient(135deg, #050816 0%, #0a1628 100%)", 
        color: "#f5f5f5",
        padding: "40px 20px"
      }}>
        <div style={{ maxWidth: "600px", margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ color: "#ef4444", marginBottom: "20px" }}>Error</h2>
          <p style={{ marginBottom: "30px" }}>{error}</p>
          <button
            onClick={() => navigate("/esg-dashboard")}
            style={{
              padding: "12px 24px",
              background: "linear-gradient(135deg, #7CFFB2, #22C55E)",
              color: "#020617",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "700"
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!company) {
    return null;
  }

  return (
    <div style={{ 
      minHeight: "100vh", 
      background: "linear-gradient(135deg, #050816 0%, #0a1628 100%)", 
      color: "#f5f5f5",
      padding: "40px 20px"
    }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "space-between",
          marginBottom: "40px",
          flexWrap: "wrap",
          gap: "20px"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <img src={LogoAppleTreeHD} alt="AppleTree" style={{ width: "50px", height: "50px" }} />
            <h1 style={{ 
              fontSize: "28px", 
              fontWeight: "800",
              background: "linear-gradient(135deg, #7CFFB2 0%, #22c55e 45%, #9d7bff 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              margin: 0
            }}>
              ESG Transparency Report
            </h1>
          </div>
          <button
            onClick={() => navigate("/esg-dashboard")}
            style={{
              padding: "10px 20px",
              background: "rgba(15,23,42,0.7)",
              color: "#f5f5f5",
              border: "1px solid rgba(148,163,184,0.4)",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600"
            }}
          >
            ← Back to Dashboard
          </button>
        </div>

        {/* Company Header */}
        <div style={{
          padding: "32px",
          background: "linear-gradient(145deg, rgba(24,163,74,0.16), rgba(5,8,22,0.92))",
          border: "1px solid rgba(124,255,178,0.4)",
          borderRadius: "16px",
          marginBottom: "30px",
          boxShadow: "0 18px 35px rgba(0,0,0,0.55)"
        }}>
          <h2 style={{ 
            fontSize: "32px", 
            fontWeight: "800", 
            color: "#E6FFE8",
            marginBottom: "12px"
          }}>
            {company.name || company.companyName}
          </h2>
          <div style={{ 
            display: "flex", 
            flexWrap: "wrap", 
            gap: "20px",
            fontSize: "14px",
            color: "#94a3b8"
          }}>
            {(company.sector || company.businessSector) && (
              <div><strong>Sector:</strong> {company.sector || company.businessSector}</div>
            )}
            <div><strong>Location:</strong> {getDisplayLocation()}</div>
            <div>
              <strong>Visibility:</strong> {getLocationVisibilityLabel(company.mapVisibility)}
            </div>
          </div>
        </div>

        {/* Certification Level */}
        {(company.level || company.certificationLevel || company.badges) && (
          <div style={{ marginBottom: "30px" }}>
            <h3 style={{ 
              fontSize: "20px", 
              fontWeight: "700",
              color: "#7CFFB2",
              marginBottom: "20px"
            }}>
              Livello di Certificazione
            </h3>
            <div style={{ 
              display: "flex", 
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
              padding: "32px",
              background: "rgba(30,41,59,0.4)",
              borderRadius: "16px",
              backdropFilter: "blur(10px)"
            }}>
              <LevelBadge level={company.level || company.certificationLevel || "NONE"} size="large" />
              {company.badges && company.badges.length > 0 && (
                <div style={{ 
                  textAlign: "center", 
                  color: "#cbd5e1", 
                  fontSize: "16px",
                  fontWeight: "500"
                }}>
                  <span style={{ fontWeight: 700, color: "#7CFFB2" }}>{company.badges.length}</span> badge ottenuti
                </div>
              )}
              {company.certificationId && (
                <button
                  onClick={() => navigate(`/verify/${company.certificationId}`)}
                  style={{
                    marginTop: "12px",
                    padding: "12px 24px",
                    background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    boxShadow: "0 4px 12px rgba(59,130,246,0.3)"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow = "0 8px 20px rgba(59,130,246,0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "0 4px 12px rgba(59,130,246,0.3)";
                  }}
                >
                  Vedi Certificazione Completa →
                </button>
              )}
            </div>
          </div>
        )}

        {/* Section Scores */}
        {(company.sectionScores || company.scores) && (
          <div style={{ marginBottom: "30px" }}>
            <h3 style={{ 
              fontSize: "20px", 
              fontWeight: "700",
              color: "#7CFFB2",
              marginBottom: "20px"
            }}>
              Detailed Section Scores
            </h3>
            
            {/* Environmental Sections */}
            {(company.sectionScores?.E || company.scores?.environmental) && (
              <div style={{ marginBottom: "24px" }}>
                <h4 style={{ fontSize: "16px", fontWeight: "600", color: "#7CFFB2", marginBottom: "12px" }}>
                  🌱 Environmental Sections
                </h4>
                <div style={{ 
                  display: "grid", 
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: "12px"
                }}>
                  {Object.entries(company.sectionScores?.E || company.scores?.environmental || {}).map(([key, value]) => (
                    <div key={key} style={{
                      padding: "12px",
                      background: "rgba(124,255,178,0.08)",
                      borderRadius: "6px",
                      border: "1px solid rgba(124,255,178,0.25)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "#94a3b8", marginBottom: "4px" }}>{key}</div>
                      <div style={{ fontSize: "20px", fontWeight: "700", color: "#7CFFB2" }}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Social Sections */}
            {(company.sectionScores?.S || company.scores?.social) && (
              <div style={{ marginBottom: "24px" }}>
                <h4 style={{ fontSize: "16px", fontWeight: "600", color: "#3b82f6", marginBottom: "12px" }}>
                  👥 Social Sections
                </h4>
                <div style={{ 
                  display: "grid", 
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: "12px"
                }}>
                  {Object.entries(company.sectionScores?.S || company.scores?.social || {}).map(([key, value]) => (
                    <div key={key} style={{
                      padding: "12px",
                      background: "rgba(59,130,246,0.08)",
                      borderRadius: "6px",
                      border: "1px solid rgba(59,130,246,0.25)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "#94a3b8", marginBottom: "4px" }}>{key}</div>
                      <div style={{ fontSize: "20px", fontWeight: "700", color: "#3b82f6" }}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Governance Sections */}
            {(company.sectionScores?.G || company.scores?.governance) && (
              <div style={{ marginBottom: "24px" }}>
                <h4 style={{ fontSize: "16px", fontWeight: "600", color: "#a855f7", marginBottom: "12px" }}>
                  ⚖️ Governance Sections
                </h4>
                <div style={{ 
                  display: "grid", 
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: "12px"
                }}>
                  {Object.entries(company.sectionScores?.G || company.scores?.governance || {}).map(([key, value]) => (
                    <div key={key} style={{
                      padding: "12px",
                      background: "rgba(168,85,247,0.08)",
                      borderRadius: "6px",
                      border: "1px solid rgba(168,85,247,0.25)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "#94a3b8", marginBottom: "4px" }}>{key}</div>
                      <div style={{ fontSize: "20px", fontWeight: "700", color: "#a855f7" }}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Certification Info */}
        <div style={{ marginBottom: "30px" }}>
          <h3 style={{ 
            fontSize: "20px", 
            fontWeight: "700",
            color: "#7CFFB2",
            marginBottom: "20px"
          }}>
            Certification Details
          </h3>
          <div style={{
            padding: "24px",
            background: "rgba(30,41,59,0.5)",
            borderRadius: "12px",
            border: "1px solid rgba(148,163,184,0.3)"
          }}>
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "16px",
              fontSize: "14px"
            }}>
              <div>
                <div style={{ color: "#94a3b8", marginBottom: "4px" }}>Issued Date</div>
                <div style={{ fontWeight: "600" }}>
                  {formatEnglishDate(company.issuedAt || company.certifiedAt || company.createdAt)}
                </div>
              </div>
              
              {company.expiresAt && (
                <div>
                  <div style={{ color: "#94a3b8", marginBottom: "4px" }}>Expires Date</div>
                  <div style={{ fontWeight: "600" }}>{formatEnglishDate(company.expiresAt)}</div>
                </div>
              )}
              
              {company.walletAddress && (
                <div>
                  <div style={{ color: "#94a3b8", marginBottom: "4px" }}>Wallet Address</div>
                  <div style={{ 
                    fontFamily: "monospace", 
                    fontSize: "12px",
                    color: "#3b82f6",
                    fontWeight: "600"
                  }}>
                    {company.walletAddress.slice(0, 10)}...{company.walletAddress.slice(-8)}
                  </div>
                </div>
              )}

              {(company.reportCID || company.ipfsCID) && (
                <div>
                  <div style={{ color: "#94a3b8", marginBottom: "4px" }}>Report CID (IPFS)</div>
                  <div style={{ 
                    fontFamily: "monospace", 
                    fontSize: "12px",
                    wordBreak: "break-all"
                  }}>
                    {(company.reportCID || company.ipfsCID).slice(0, 20)}...
                  </div>
                </div>
              )}
            </div>

            {/* IPFS Link */}
            {(company.reportCID || company.ipfsCID) && (
              <div style={{ marginTop: "20px" }}>
                <a
                  href={getIpfsUrl(company.reportCID || company.ipfsCID)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "12px 20px",
                    background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                    color: "white",
                    borderRadius: "8px",
                    textDecoration: "none",
                    fontSize: "14px",
                    fontWeight: "600",
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = "scale(1.05)";
                    e.target.style.boxShadow = "0 8px 16px rgba(139,92,246,0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "scale(1)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  📄 View Raw Report (IPFS)
                </a>
              </div>
            )}

            {/* On-chain verification */}
            {(company.txHash || company.transactionHash) && (
              <div style={{ marginTop: "12px" }}>
                <a
                  href={`https://etherscan.io/tx/${company.txHash || company.transactionHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "12px 20px",
                    background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                    color: "white",
                    borderRadius: "8px",
                    textDecoration: "none",
                    fontSize: "14px",
                    fontWeight: "600",
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = "scale(1.05)";
                    e.target.style.boxShadow = "0 8px 16px rgba(59,130,246,0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "scale(1)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  ⛓️ Verify On-Chain (Etherscan)
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Privacy Notice */}
        <div style={{
          padding: "16px",
          background: "rgba(59,130,246,0.08)",
          border: "1px solid rgba(59,130,246,0.25)",
          borderRadius: "8px",
          fontSize: "12px",
          color: "#93c5fd"
        }}>
          <strong>🔒 Privacy Note:</strong> Location information is displayed according to the company's 
          transparency consent preference ({getLocationVisibilityLabel(company.mapVisibility)}). 
          Full address details are only shown with explicit consent.
        </div>
      </div>
      <LegalFooter />
    </div>
  );
};

export default CompanyDetail;
